import React, { useState } from 'react';
import { Trash2Icon } from 'lucide-react';
import type { Granularity, Milestone } from '../types/life';
import { cn } from '../utils/cn';
import { formatCountdown, formatElapsed } from '../utils/format';
import { milestoneStart, milestoneUnitLabel } from '../utils/lifeGrid';
import { useNow } from '../hooks/useNow';
import { Modal } from './Modal';
export interface MilestoneDraft {
  id?: string;
  title: string;
  granularity: Granularity;
  date: string;
}
interface MilestoneDialogProps {
  open: boolean;
  draft: MilestoneDraft | null;
  onClose: () => void;
  onSave: (milestone: Milestone) => void;
  onDelete: (id: string) => void;
}
const LEVELS: Array<{
  id: Granularity;
  label: string;
}> = [{
  id: 'days',
  label: 'Day'
}, {
  id: 'weeks',
  label: 'Week'
}, {
  id: 'months',
  label: 'Month'
}, {
  id: 'years',
  label: 'Year'
}];
const inputClass = 'w-full border border-line bg-paper px-3 py-2.5 text-sm text-ink outline-none transition-colors duration-150 ease-swift focus:border-accent';
function newId(): string {
  return `ms-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}
function MilestoneForm({
  draft,
  onClose,
  onSave,
  onDelete
}: Omit<MilestoneDialogProps, 'open'> & {
  draft: MilestoneDraft;
}) {
  const now = useNow(1000);
  const [title, setTitle] = useState(draft.title);
  const [granularity, setGranularity] = useState<Granularity>(draft.granularity);
  const [date, setDate] = useState(draft.date);
  const candidate: Milestone = {
    id: draft.id ?? 'preview',
    title,
    granularity,
    date
  };
  const start = milestoneStart(candidate);
  const ahead = start.getTime() > now.getTime();
  const valid = Boolean(date) && title.trim().length > 0;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onSave({
      id: draft.id ?? newId(),
      title: title.trim(),
      granularity,
      date
    });
  };
  return <form onSubmit={submit} className="space-y-6" data-id="element-15" data-mp="2bou4b78q5fne">
      <div className="space-y-2" data-id="element-16" data-mp="2bnizr57gg3v8">
        <label htmlFor="ms-title" className="text-muted block text-xs font-medium" data-id="element-17" data-mp="23oec2qt0h4zu">
          What will happen
        </label>
        <input id="ms-title" autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. thesis defense" className={inputClass} required data-id="element-18" data-mp="1znm19t4hqs1g" />
      </div>

      <div className="space-y-2" data-id="element-19" data-mp="28h7e36x3hz88">
        <span className="text-muted block text-xs font-medium" data-id="element-20" data-mp="1kp0c9s8lp01f">Level</span>
        <div className="border-line flex border p-1" role="group" aria-label="Milestone level" data-id="element-21" data-mp="1krd1mqgoucoi">
          {LEVELS.map(level => {
          const active = level.id === granularity;
          return <button key={level.id} type="button" aria-pressed={active} onClick={() => setGranularity(level.id)} className={cn('flex-1 px-3 py-1.5 text-xs font-medium transition-colors duration-150 ease-swift', active ? 'bg-ink text-paper' : 'text-muted hover:text-ink')} data-id="element-22" data-mp="1pomtecrjv0fi">
                {level.label}
              </button>;
        })}
        </div>
      </div>

      <div className="space-y-2" data-id="element-23" data-mp="dw4979fhhlno">
        <label htmlFor="ms-date" className="text-muted block text-xs font-medium" data-id="element-24" data-mp="dx6xd9lf99zw">
          A date inside that unit
        </label>
        <input id="ms-date" type="date" value={date} onChange={e => setDate(e.target.value)} className={cn(inputClass, 'font-mono')} required data-id="element-25" data-mp="16k00tzq74ndl" />
      </div>

      {date && <div className="border-line flex items-baseline justify-between gap-4 border-t pt-4" data-id="element-26" data-mp="wtf64ndn82zy">
          <span className="text-muted text-xs" data-id="element-27" data-mp="11s9oxpbm1rk2">{milestoneUnitLabel(candidate)}</span>
          <span className={cn('font-mono text-xs tabular-nums', ahead ? 'text-accent' : 'text-muted')} data-id="element-28" data-mp="1pim2pgtjeiik">
            {ahead ? formatCountdown(start, now) : formatElapsed(start, now)}
          </span>
        </div>}

      <div className="flex items-center justify-between gap-3" data-id="element-29" data-mp="5okm1egtqfq8">
        <div className="flex items-center gap-3" data-id="element-30" data-mp="o6q2ushyw8k">
          <button type="submit" disabled={!valid} className="bg-ink text-paper hover:bg-accent disabled:bg-line disabled:text-muted px-5 py-2.5 text-sm font-semibold transition-colors duration-150 ease-swift disabled:cursor-not-allowed" data-id="element-31" data-mp="ndengilfik3">
            Save
          </button>
          <button type="button" onClick={onClose} className="text-muted hover:text-ink px-2 text-sm transition-colors duration-150 ease-swift" data-id="element-32" data-mp="og3nvhz43nqo">
            Cancel
          </button>
        </div>
        {draft.id && <button type="button" onClick={() => onDelete(draft.id as string)} className="text-muted hover:text-ink transition-colors duration-150 ease-swift" aria-label="Delete milestone" data-id="element-33" data-mp="amcq16uqtsas">
            <Trash2Icon className="h-4 w-4" data-id="element-34" data-mp="1amy0r68enno6" />
          </button>}
      </div>
    </form>;
}
export function MilestoneDialog({
  open,
  draft,
  onClose,
  onSave,
  onDelete
}: MilestoneDialogProps) {
  return <Modal open={open && Boolean(draft)} title={draft?.id ? 'Milestone' : 'New milestone'} onClose={onClose} data-id="element-35" data-mp="1kkcg12ile6ew">
      {draft && <MilestoneForm key={draft.id ?? `${draft.granularity}-${draft.date}`} draft={draft} onClose={onClose} onSave={onSave} onDelete={onDelete} data-id="element-36" data-mp="1on8sebhjh6ae" />}
    </Modal>;
}